import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  Settings, 
  BookOpen, 
  Database, 
  Download, 
  Upload, 
  Trash2, 
  Eye, 
  EyeOff,
  BarChart3,
  FileText,
  Tag,
  Folder,
  Plus,
  Edit,
  X,
  Save,
  Search,
  Filter,
  CheckSquare,
  Square,
  AlertCircle,
  CheckCircle,
  Info,
  Volume2,
  Play,
  Pause,
  VolumeX,
  ChevronDown,
  ChevronUp,
  Check
} from 'lucide-react';
import { glossaryService, GlossaryEntry } from '../services/glossaryService';
import { keywordService } from '../services/keywordService';
import AudioService from '../services/audioService';
import { apiService } from '../services/api';
import { useAuth } from '../contexts/AuthContext';
import { cn } from '../utils/cn';
import toast from 'react-hot-toast';

const SettingsPage: React.FC = () => {
  const { user } = useAuth();
  const isAdminOrInstructor = user?.roles?.some((role: string) => 
    role === 'admin' || role === 'instructor'
  ) || false;
  
  const [activeTab, setActiveTab] = useState<'overview' | 'glossary' | 'voiceover'>('overview');
  const [keywordFeatureEnabled, setKeywordFeatureEnabled] = useState(keywordService.isEnabled());
  const [statistics, setStatistics] = useState({ total: 0, active: 0, inactive: 0, categories: 0, tags: 0 });
  const [categories, setCategories] = useState<string[]>([]);
  const [tags, setTags] = useState<string[]>([]);
  const [glossaryStatusFilter, setGlossaryStatusFilter] = useState<'all' | 'pending' | 'approved' | 'rejected' | 'needs_review'>('all');
  const [showRejected, setShowRejected] = useState(false);
  const [reviewMode, setReviewMode] = useState(false);
  const [isLoadingGlossary, setIsLoadingGlossary] = useState(false);
  const [showHowItWorks, setShowHowItWorks] = useState(false);
  const [showStatusHelp, setShowStatusHelp] = useState(false);
  // Shepherd tours removed
  
  // Voiceover settings state
  const [voiceoverSettings, setVoiceoverSettings] = useState({
    provider: 'elevenlabs' as 'elevenlabs' | 'browser',
    voice: 'pNInz6obpgDQGcFmaJgB', // Default Adam voice
    speed: 1.0,
    pitch: 1.0,
    volume: 1.0,
    enabled: true
  });
  const [availableVoices, setAvailableVoices] = useState<Array<{id: string, name: string, provider: string}>>([]);
  const [isLoadingVoices, setIsLoadingVoices] = useState(false);
  const [testText, setTestText] = useState('Hello! This is a test of the voiceover settings. You can adjust the voice, speed, and pitch to your preference.');
  
  // Glossary management state
  const [glossaryEntries, setGlossaryEntries] = useState<GlossaryEntry[]>([]);
  const [filteredEntries, setFilteredEntries] = useState<GlossaryEntry[]>([]);
  const [selectedEntries, setSelectedEntries] = useState<Set<string>>(new Set());
  const [searchQuery, setSearchQuery] = useState('');
  const [categoryFilter, setCategoryFilter] = useState('');
  const [tagFilter, setTagFilter] = useState('');
  const [statusFilter, setStatusFilter] = useState<'all' | 'active' | 'inactive'>('all');
  const [showImportModal, setShowImportModal] = useState(false);
  const [importData, setImportData] = useState('');
  const [importResults, setImportResults] = useState<{ success: number; errors: string[] } | null>(null);
  const [editingEntry, setEditingEntry] = useState<GlossaryEntry | null>(null);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showAddModal, setShowAddModal] = useState(false);
  const [newEntry, setNewEntry] = useState({
    term: '',
    definition: '',
    category: '',
    aliases: '',
    tags: '',
    image: '',
    isActive: true
  });


  useEffect(() => {
    loadData();
    loadVoiceoverSettings();
    loadAvailableVoices();
  }, []);

  useEffect(() => {
    filterGlossaryEntries();
  }, [glossaryEntries, searchQuery, categoryFilter, tagFilter, statusFilter, glossaryStatusFilter]);

  const loadData = async () => {
    setIsLoadingGlossary(true);
    try {
      // Use API service to get ALL glossary entries (including pending, needs_review, etc.)
      // For admin/instructor, we want all entries regardless of status
      // The backend will return all entries for admin/instructor, or only approved for regular users
      const response = await apiService.getGlossary(false, false);
      console.log('Glossary loadData response:', { 
        success: response.success, 
        dataLength: response.data?.length,
        isAdminOrInstructor,
        sampleEntry: response.data?.[0],
        statusCounts: response.data?.reduce((acc: any, e: any) => {
          acc[e.status || 'unknown'] = (acc[e.status || 'unknown'] || 0) + 1;
          return acc;
        }, {})
      });
      if (response.success && response.data) {
        const entries = response.data.map((entry: any) => ({
          ...entry,
          aliases: typeof entry.aliases === 'string' ? JSON.parse(entry.aliases) : entry.aliases || [],
          tags: typeof entry.tags === 'string' ? JSON.parse(entry.tags) : entry.tags || []
        }));
        
        console.log('Processed entries:', {
          total: entries.length,
          byStatus: entries.reduce((acc: any, e: any) => {
            acc[e.status || 'unknown'] = (acc[e.status || 'unknown'] || 0) + 1;
            return acc;
          }, {})
        });
        
        // Calculate statistics
        const statistics = {
          total: entries.length,
          active: entries.filter((e: any) => e.isActive).length,
          inactive: entries.filter((e: any) => !e.isActive).length,
          categories: new Set(entries.map((e: any) => e.category).filter(Boolean)).size,
          tags: new Set(entries.flatMap((e: any) => e.tags || [])).size
        };
        
        setStatistics(statistics);
        setGlossaryEntries(entries);
        setCategories(Array.from(new Set(entries.map((e: any) => e.category).filter(Boolean))));
        setTags(Array.from(new Set(entries.flatMap((e: any) => e.tags || []))));
        
        // Auto-select "Pending Review" if there are items that need review and we're on "All"
        if (glossaryStatusFilter === 'all' && isAdminOrInstructor) {
          const pendingCount = entries.filter((e: any) => e.status === 'pending' || e.status === 'needs_review').length;
          if (pendingCount > 0) {
            setGlossaryStatusFilter('pending');
          }
        }
      } else {
        // Fallback to glossaryService
        const [statistics, entries, categories, tags] = await Promise.all([
          glossaryService.getStatistics(),
          glossaryService.getAllEntries(),
          glossaryService.getCategories(),
          glossaryService.getTags()
        ]);
        setStatistics(statistics);
        setGlossaryEntries(entries);
        setCategories(categories);
        setTags(tags);
      }
    } catch (error) {
      console.error('Error loading glossary data:', error);
      setStatistics({ total: 0, active: 0, inactive: 0, categories: 0, tags: 0 });
      setGlossaryEntries([]);
      setCategories([]);
      setTags([]);
    } finally {
      setIsLoadingGlossary(false);
    }
  };

  const handleToggleKeywordFeature = () => {
    const newState = !keywordFeatureEnabled;
    setKeywordFeatureEnabled(newState);
    keywordService.setEnabled(newState);
  };

  // Shepherd tours removed

  const handleGlossaryUpdated = async () => {
    await loadData();
    await keywordService.refreshFromGlossary();
  };

  const filterGlossaryEntries = () => {
    // Safety check: ensure glossaryEntries is an array
    if (!Array.isArray(glossaryEntries)) {
      setFilteredEntries([]);
      return;
    }
    
    let filtered = [...glossaryEntries];

    // Filter by status (for admin/instructor)
    if (isAdminOrInstructor && glossaryStatusFilter !== 'all') {
      if (glossaryStatusFilter === 'pending') {
        // Show both 'pending' and 'needs_review' when filtering by 'pending'
        filtered = filtered.filter((entry: any) => entry.status === 'pending' || entry.status === 'needs_review');
      } else {
        filtered = filtered.filter((entry: any) => entry.status === glossaryStatusFilter);
      }
    } else if (!isAdminOrInstructor) {
      // Non-admin users only see approved items
      filtered = filtered.filter((entry: any) => entry.status === 'approved');
    }
    
    // Hide rejected items by default unless explicitly showing them
    if (!showRejected && isAdminOrInstructor) {
      filtered = filtered.filter((entry: any) => entry.status !== 'rejected');
    }

    if (searchQuery.trim()) {
      const query = searchQuery.toLowerCase();
      filtered = filtered.filter(entry =>
        entry.term.toLowerCase().includes(query) ||
        entry.definition.toLowerCase().includes(query) ||
        entry.aliases?.some((alias: string) => alias.toLowerCase().includes(query)) ||
        entry.tags?.some((tag: string) => tag.toLowerCase().includes(query))
      );
    }

    if (categoryFilter) {
      filtered = filtered.filter(entry => entry.category === categoryFilter);
    }

    if (tagFilter) {
      filtered = filtered.filter(entry => 
        entry.tags?.some((tag: string) => tag === tagFilter)
      );
    }

    if (statusFilter !== 'all') {
      const isActive = statusFilter === 'active';
      filtered = filtered.filter(entry => entry.isActive === isActive);
    }

    setFilteredEntries(filtered);
  };

  const handleExportGlossary = () => {
    const data = glossaryService.exportEntries();
    const blob = new Blob([JSON.stringify(data, null, 2)], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `glossary-export-${new Date().toISOString().split('T')[0]}.json`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  const handleClearGlossary = () => {
    if (confirm('Are you sure you want to clear all glossary entries? This action cannot be undone.')) {
      glossaryService.clearAllEntries();
      handleGlossaryUpdated();
    }
  };

  const handleImportGlossary = () => {
    try {
      const data = JSON.parse(importData);
      const results = glossaryService.importEntries(data);
      setImportResults(results);
      
      if (results.success > 0) {
        handleGlossaryUpdated();
        setImportData('');
      }
    } catch (error) {
      setImportResults({ success: 0, errors: ['Invalid JSON format'] });
    }
  };

  const handleAddGlossaryEntry = async () => {
    if (!newEntry.term || !newEntry.definition) {
      toast.error('Term and definition are required.');
      return;
    }

    const aliasesArray = newEntry.aliases.split(',').map(a => a.trim()).filter(a => a.length > 0);
    const tagsArray = newEntry.tags.split(',').map(t => t.trim()).filter(t => t.length > 0);

    try {
      // If admin/instructor, auto-approve. Otherwise, submit for approval
      if (isAdminOrInstructor) {
        // Submit and immediately approve
        const submitResponse = await apiService.submitGlossaryEntries([{
          term: newEntry.term,
          definition: newEntry.definition,
          category: newEntry.category || undefined,
          aliases: aliasesArray,
          tags: tagsArray.length > 0 ? tagsArray : ['manual-entry']
        }]);
        
        // Check for ids in both data.ids and top-level ids (depending on API response structure)
        const entryIds = submitResponse.data?.ids || (submitResponse as any).ids;
        if (submitResponse.success && entryIds && entryIds.length > 0) {
          // Auto-approve for admins/instructors
          const approveResponse = await apiService.approveGlossaryEntries(entryIds);
          if (approveResponse.success) {
            toast.success('Entry added and approved successfully');
            setShowAddModal(false);
            await loadData();
            setNewEntry({
              term: '',
              definition: '',
              category: '',
              aliases: '',
              tags: '',
              image: '',
              isActive: true
            });
          } else {
            toast.error('Entry added but failed to approve. Please approve manually.');
            setShowAddModal(false);
            await loadData();
            setNewEntry({
              term: '',
              definition: '',
              category: '',
              aliases: '',
              tags: '',
              image: '',
              isActive: true
            });
          }
        } else {
          toast.error('Failed to add entry. Please try again.');
        }
      } else {
        // Regular users submit for approval
        const response = await apiService.submitGlossaryEntries([{
          term: newEntry.term,
          definition: newEntry.definition,
          category: newEntry.category || undefined,
          aliases: aliasesArray,
          tags: tagsArray.length > 0 ? tagsArray : ['manual-entry']
        }]);
        
        if (response.success) {
          toast.success('Entry submitted for approval');
          setShowAddModal(false);
          await loadData();
          setNewEntry({
            term: '',
            definition: '',
            category: '',
            aliases: '',
            tags: '',
            image: '',
            isActive: true
          });
        } else {
          toast.error('Failed to submit entry. Please try again.');
        }
      }
    } catch (error: any) {
      console.error('Add glossary entry error:', error);
      toast.error(error.message || 'Failed to add entry');
    }
  };

  const handleDeleteGlossaryEntry = (id: string) => {
    if (confirm('Are you sure you want to delete this entry?')) {
      glossaryService.deleteEntry(id);
      handleGlossaryUpdated();
    }
  };

  const handleBulkDeleteGlossary = async () => {
    if (selectedEntries.size === 0) {
      toast.error('Please select entries to delete.');
      return;
    }

    if (confirm(`Are you sure you want to delete ${selectedEntries.size} entries?`)) {
      try {
        const deletedCount = glossaryService.deleteEntries(Array.from(selectedEntries));
        setSelectedEntries(new Set());
        await loadData();
        toast.success(`Deleted ${deletedCount} entries.`);
      } catch (error) {
        toast.error('Failed to delete entries');
      }
    }
  };

  const handleBulkApproveGlossary = async () => {
    if (selectedEntries.size === 0) {
      toast.error('Please select entries to approve.');
      return;
    }

    if (!isAdminOrInstructor) {
      toast.error('Only admins and instructors can approve entries.');
      return;
    }

    try {
      const response = await apiService.approveGlossaryEntries(Array.from(selectedEntries));
      if (response.success) {
        setSelectedEntries(new Set());
        await loadData();
        toast.success(`Approved ${response.count || selectedEntries.size} entries.`);
      } else {
        toast.error('Failed to approve entries');
      }
    } catch (error: any) {
      toast.error(error.message || 'Failed to approve entries');
    }
  };

  const handleBulkRejectGlossary = async () => {
    if (selectedEntries.size === 0) {
      toast.error('Please select entries to reject.');
      return;
    }

    if (!isAdminOrInstructor) {
      toast.error('Only admins and instructors can reject entries.');
      return;
    }

    if (confirm(`Are you sure you want to reject ${selectedEntries.size} entries?`)) {
      try {
        const response = await apiService.rejectGlossaryEntries(Array.from(selectedEntries));
        if (response.success) {
          setSelectedEntries(new Set());
          await loadData();
          toast.success(`Rejected ${response.count || selectedEntries.size} entries.`);
        } else {
          toast.error('Failed to reject entries');
        }
      } catch (error: any) {
        toast.error(error.message || 'Failed to reject entries');
      }
    }
  };

  const handleApproveAllFiltered = async () => {
    if (filteredEntries.length === 0) {
      toast.error('No entries to approve.');
      return;
    }

    if (!isAdminOrInstructor) {
      toast.error('Only admins and instructors can approve entries.');
      return;
    }

    const count = filteredEntries.length;
    if (confirm(`Approve all ${count} entries in the current view? This will approve all filtered entries.`)) {
      try {
        const ids = filteredEntries.map(e => e.id);
        const response = await apiService.approveGlossaryEntries(ids);
        if (response.success) {
          setSelectedEntries(new Set());
          await loadData();
          toast.success(`Approved ${response.count || count} entries.`);
        } else {
          toast.error('Failed to approve entries');
        }
      } catch (error: any) {
        toast.error(error.message || 'Failed to approve entries');
      }
    }
  };

  const handleRejectAllFiltered = async () => {
    if (filteredEntries.length === 0) {
      toast.error('No entries to reject.');
      return;
    }

    if (!isAdminOrInstructor) {
      toast.error('Only admins and instructors can reject entries.');
      return;
    }

    const count = filteredEntries.length;
    if (confirm(`Reject all ${count} entries in the current view? This will reject all filtered entries.`)) {
      try {
        const ids = filteredEntries.map(e => e.id);
        const response = await apiService.rejectGlossaryEntries(ids);
        if (response.success) {
          setSelectedEntries(new Set());
          await loadData();
          toast.success(`Rejected ${response.count || count} entries.`);
        } else {
          toast.error('Failed to reject entries');
        }
      } catch (error: any) {
        toast.error(error.message || 'Failed to reject entries');
      }
    }
  };

  const handleToggleGlossarySelection = (id: string) => {
    const newSelection = new Set(selectedEntries);
    if (newSelection.has(id)) {
      newSelection.delete(id);
    } else {
      newSelection.add(id);
    }
    setSelectedEntries(newSelection);
  };

  const handleSelectAllGlossary = () => {
    if (selectedEntries.size === filteredEntries.length) {
      setSelectedEntries(new Set());
    } else {
      setSelectedEntries(new Set(filteredEntries.map(entry => entry.id)));
    }
  };

  const handleEditGlossaryEntry = (entry: GlossaryEntry) => {
    setEditingEntry(entry);
    setNewEntry({
      term: entry.term,
      definition: entry.definition,
      category: entry.category || '',
      aliases: entry.aliases?.join(', ') || '',
      tags: entry.tags?.join(', ') || '',
      isActive: entry.isActive
    });
    setShowEditModal(true);
  };

  const handleSaveGlossaryEntry = () => {
    if (!editingEntry || !newEntry.term || !newEntry.definition) {
      alert('Term and definition are required.');
      return;
    }

    const aliasesArray = newEntry.aliases.split(',').map(a => a.trim()).filter(a => a.length > 0);
    const tagsArray = newEntry.tags.split(',').map(t => t.trim()).filter(t => t.length > 0);

    const updatedEntry: GlossaryEntry = {
      ...editingEntry,
      term: newEntry.term,
      definition: newEntry.definition,
      category: newEntry.category || undefined,
      aliases: aliasesArray.length > 0 ? aliasesArray : undefined,
      tags: tagsArray.length > 0 ? tagsArray : undefined,
      isActive: newEntry.isActive,
      updatedAt: new Date().toISOString()
    };

    const success = glossaryService.updateEntry(updatedEntry.id, {
      term: updatedEntry.term,
      definition: updatedEntry.definition,
      category: updatedEntry.category,
      aliases: updatedEntry.aliases,
      tags: updatedEntry.tags,
      isActive: updatedEntry.isActive
    });
    
    if (success) {
      handleGlossaryUpdated();
    } else {
      alert('Failed to update entry. Please try again.');
    }
    setEditingEntry(null);
    setShowEditModal(false);
    setNewEntry({
      term: '',
      definition: '',
      category: '',
      aliases: '',
      tags: '',
      image: '',
      isActive: true
    });
  };

  const handleCancelEdit = () => {
    setEditingEntry(null);
    setShowEditModal(false);
    setNewEntry({
      term: '',
      definition: '',
      category: '',
      aliases: '',
      tags: '',
      image: '',
      isActive: true
    });
  };

  // Voiceover settings functions
  const loadVoiceoverSettings = () => {
    const saved = localStorage.getItem('voiceoverSettings');
    if (saved) {
      setVoiceoverSettings(JSON.parse(saved));
    }
  };

  const saveVoiceoverSettings = (settings: typeof voiceoverSettings) => {
    localStorage.setItem('voiceoverSettings', JSON.stringify(settings));
    setVoiceoverSettings(settings);
  };

  const loadAvailableVoices = async () => {
    setIsLoadingVoices(true);
    try {
      const audioService = AudioService.getInstance();
      const voices = await audioService.getAvailableVoices();
      // Deduplicate by provider+id to avoid duplicate React keys/options
      const seen = new Set<string>();
      const unique: Array<{ id: string; name: string; provider: string }> = [];
      voices.forEach((v: { id: string; name: string; provider: string }) => {
        const k = `${v.provider}-${v.id}`;
        if (!seen.has(k)) {
          seen.add(k);
          unique.push(v);
        }
      });
      setAvailableVoices(unique);
    } catch (error) {
      console.error('Failed to load voices:', error);
    } finally {
      setIsLoadingVoices(false);
    }
  };

  const handleVoiceoverSettingChange = (key: string, value: any) => {
    const newSettings = { ...voiceoverSettings, [key]: value };
    saveVoiceoverSettings(newSettings);
  };

  const testVoiceover = async () => {
    try {
      const audioService = AudioService.getInstance();
      await audioService.generateAudio(testText, {
        provider: voiceoverSettings.provider,
        voice: voiceoverSettings.voice,
        speed: voiceoverSettings.speed,
        pitch: voiceoverSettings.pitch,
        volume: voiceoverSettings.volume
      });
    } catch (error) {
      console.error('Voiceover test failed:', error);
      alert('Voiceover test failed. Please check your settings.');
    }
  };



  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Header */}
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-gray-900 dark:text-white flex items-center space-x-3">
            <Settings className="w-8 h-8 text-blue-600 dark:text-blue-400" />
            <span>Settings</span>
          </h1>
          <p className="mt-2 text-gray-600 dark:text-gray-400">
            Manage your application settings, glossary entries, and keyword definitions.
          </p>
        </div>

        {/* Tab Navigation */}
        <div className="mb-8">
          <div className="border-b border-gray-200 dark:border-gray-700">
            <nav className="-mb-px flex space-x-8">
                     {[
                       { id: 'overview', name: 'Overview', icon: BarChart3 },
                       { id: 'glossary', name: 'Glossary & Keywords', icon: Database },
                       { id: 'voiceover', name: 'Voiceover Settings', icon: Volume2 }
                     ].map((tab) => {
                const Icon = tab.icon;
                return (
                  <button
                    key={tab.id}
                    onClick={() => setActiveTab(tab.id as any)}
                    className={cn(
                      "flex items-center space-x-2 py-2 px-1 border-b-2 font-medium text-sm transition-colors",
                      activeTab === tab.id
                        ? "border-blue-500 text-blue-600 dark:text-blue-400"
                        : "border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300 dark:text-gray-400 dark:hover:text-gray-300"
                    )}
                  >
                    <Icon className="w-5 h-5" />
                    <span>{tab.name}</span>
                  </button>
                );
              })}
            </nav>
          </div>
        </div>

        {/* Tab Content */}
        <AnimatePresence mode="wait">
          {activeTab === 'overview' && (
            <motion.div
              key="overview"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -20 }}
              className="space-y-8"
            >
              {/* Statistics Cards */}
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
                <div className="bg-white dark:bg-gray-800 rounded-lg shadow-md p-6">
                  <div className="flex items-center">
                    <div className="p-3 bg-blue-100 dark:bg-blue-900/20 rounded-lg">
                      <FileText className="w-6 h-6 text-blue-600 dark:text-blue-400" />
                    </div>
                    <div className="ml-4">
                      <p className="text-sm font-medium text-gray-600 dark:text-gray-400">Total Entries</p>
                      <p className="text-2xl font-bold text-gray-900 dark:text-white">{statistics.total}</p>
                    </div>
                  </div>
                </div>

                <div className="bg-white dark:bg-gray-800 rounded-lg shadow-md p-6">
                  <div className="flex items-center">
                    <div className="p-3 bg-green-100 dark:bg-green-900/20 rounded-lg">
                      <Eye className="w-6 h-6 text-green-600 dark:text-green-400" />
                    </div>
                    <div className="ml-4">
                      <p className="text-sm font-medium text-gray-600 dark:text-gray-400">Active Entries</p>
                      <p className="text-2xl font-bold text-gray-900 dark:text-white">{statistics.active}</p>
                    </div>
                  </div>
                </div>

                <div className="bg-white dark:bg-gray-800 rounded-lg shadow-md p-6">
                  <div className="flex items-center">
                    <div className="p-3 bg-purple-100 dark:bg-purple-900/20 rounded-lg">
                      <Folder className="w-6 h-6 text-purple-600 dark:text-purple-400" />
                    </div>
                    <div className="ml-4">
                      <p className="text-sm font-medium text-gray-600 dark:text-gray-400">Categories</p>
                      <p className="text-2xl font-bold text-gray-900 dark:text-white">{statistics.categories}</p>
                    </div>
                  </div>
                </div>

                <div className="bg-white dark:bg-gray-800 rounded-lg shadow-md p-6">
                  <div className="flex items-center">
                    <div className="p-3 bg-orange-100 dark:bg-orange-900/20 rounded-lg">
                      <Tag className="w-6 h-6 text-orange-600 dark:text-orange-400" />
                    </div>
                    <div className="ml-4">
                      <p className="text-sm font-medium text-gray-600 dark:text-gray-400">Tags</p>
                      <p className="text-2xl font-bold text-gray-900 dark:text-white">{statistics.tags}</p>
                    </div>
                  </div>
                </div>
              </div>

              {/* Quick Actions */}
              <div className="bg-white dark:bg-gray-800 rounded-lg shadow-md p-6">
                <h2 className="text-xl font-semibold text-gray-900 dark:text-white mb-4 flex items-center space-x-2">
                  <BarChart3 className="w-6 h-6 text-purple-600 dark:text-purple-400" />
                  <span>Quick Actions</span>
                </h2>
                
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <div className="p-4 bg-blue-50 dark:bg-blue-900/20 rounded-lg">
                    <h3 className="font-medium text-blue-900 dark:text-blue-100 mb-2">Import Glossary</h3>
                    <p className="text-sm text-blue-700 dark:text-blue-300 mb-3">
                      Import glossary entries from a JSON file to quickly populate your glossary.
                    </p>
                    <button
                      onClick={() => setActiveTab('glossary')}
                      className="text-sm text-blue-600 dark:text-blue-400 hover:text-blue-800 dark:hover:text-blue-200 font-medium"
                    >
                      Start Import →
                    </button>
                  </div>
                  
                  <div className="p-4 bg-green-50 dark:bg-green-900/20 rounded-lg">
                    <h3 className="font-medium text-green-900 dark:text-green-100 mb-2">Backup Data</h3>
                    <p className="text-sm text-green-700 dark:text-green-300 mb-3">
                      Export your glossary data as a backup or to share with other team members.
                    </p>
                    <button
                      onClick={handleExportGlossary}
                      className="text-sm text-green-600 dark:text-green-400 hover:text-green-800 dark:hover:text-green-200 font-medium"
                    >
                      Export Now →
                    </button>
                  </div>
                  
                  <div className="p-4 bg-purple-50 dark:bg-purple-900/20 rounded-lg">
                    <h3 className="font-medium text-purple-900 dark:text-purple-100 mb-2">Manage Keywords</h3>
                    <p className="text-sm text-purple-700 dark:text-purple-300 mb-3">
                      Configure keyword highlighting and manage technical term definitions.
                    </p>
                    <button
                      onClick={() => setActiveTab('keywords')}
                      className="text-sm text-purple-600 dark:text-purple-400 hover:text-purple-800 dark:hover:text-purple-200 font-medium"
                    >
                      Manage Keywords →
                    </button>
                  </div>
                </div>
              </div>

              {/* Shepherd tours removed */}
            </motion.div>
          )}

          {activeTab === 'glossary' && (
            <motion.div
              key="glossary"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -20 }}
              className="space-y-6"
            >
              {/* Glossary Management Header */}
              <div className="bg-white dark:bg-gray-800 rounded-lg shadow-md p-6 mb-6">
                <div className="flex items-start justify-between mb-6">
                  <div>
                    <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-2">Glossary Management</h2>
                    <p className="text-sm text-gray-600 dark:text-gray-400">
                      Manage technical terms that appear in tutorials. Only approved terms are highlighted for users.
                    </p>
                  </div>
                  <div className="flex flex-wrap gap-2">
                    <button
                      onClick={loadData}
                      disabled={isLoadingGlossary}
                      className="flex items-center space-x-2 px-3 py-2 bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 rounded-lg hover:bg-gray-200 dark:hover:bg-gray-600 transition-colors disabled:opacity-50"
                      title="Refresh glossary data"
                    >
                      {isLoadingGlossary ? (
                        <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-gray-600"></div>
                      ) : (
                        <Database className="w-4 h-4" />
                      )}
                      <span>Refresh</span>
                    </button>
                    <button
                      onClick={() => setShowImportModal(true)}
                      className="flex items-center space-x-2 px-3 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors"
                    >
                      <Upload className="w-4 h-4" />
                      <span>Import</span>
                    </button>
                    <button
                      onClick={handleExportGlossary}
                      className="flex items-center space-x-2 px-3 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
                    >
                      <Download className="w-4 h-4" />
                      <span>Export</span>
                    </button>
                    {isAdminOrInstructor && (
                      <button
                        onClick={handleClearGlossary}
                        className="flex items-center space-x-2 px-3 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors"
                      >
                        <Trash2 className="w-4 h-4" />
                        <span>Clear All</span>
                      </button>
                    )}
                  </div>
                </div>

                {/* Status Tabs (Admin/Instructor only) */}
                {isAdminOrInstructor && (
                  <div className="border-t border-gray-200 dark:border-gray-700 pt-4">
                    <div className="flex items-center justify-between mb-3">
                      <h3 className="text-sm font-semibold text-gray-700 dark:text-gray-300 uppercase tracking-wide">Filter by Status</h3>
                      <button
                        onClick={() => setShowStatusHelp(!showStatusHelp)}
                        className="text-xs text-purple-600 dark:text-purple-400 hover:text-purple-700 flex items-center space-x-1"
                      >
                        {showStatusHelp ? <ChevronUp className="w-3 h-3" /> : <ChevronDown className="w-3 h-3" />}
                        <span>{showStatusHelp ? 'Hide' : 'Show'} Help</span>
                      </button>
                    </div>
                    <div className="flex flex-wrap gap-2 mb-3">
                      {(['all', 'pending', 'approved'] as const).map((status) => {
                        let count: number;
                        if (status === 'all') {
                          // Count all except rejected (unless showRejected is true)
                          count = showRejected 
                            ? glossaryEntries.length
                            : glossaryEntries.filter((e: any) => e.status !== 'rejected').length;
                        } else if (status === 'pending') {
                          // Count both 'pending' and 'needs_review' together
                          count = glossaryEntries.filter((e: any) => e.status === 'pending' || e.status === 'needs_review').length;
                        } else {
                          count = glossaryEntries.filter((e: any) => e.status === status).length;
                        }
                        const isActive = glossaryStatusFilter === status;
                        return (
                          <button
                            key={status}
                            onClick={() => {
                              setGlossaryStatusFilter(status);
                              if (status !== 'all') setShowRejected(false);
                            }}
                            className={cn(
                              "px-4 py-2 text-sm font-medium rounded-lg transition-all flex items-center space-x-2 border-2",
                              isActive && status === 'pending' && "border-yellow-500 bg-yellow-50 dark:bg-yellow-900/20 text-yellow-700 dark:text-yellow-300",
                              isActive && status === 'approved' && "border-green-500 bg-green-50 dark:bg-green-900/20 text-green-700 dark:text-green-300",
                              isActive && status === 'all' && "border-purple-500 bg-purple-50 dark:bg-purple-900/20 text-purple-700 dark:text-purple-300",
                              !isActive && "border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 text-gray-600 dark:text-gray-400 hover:border-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700"
                            )}
                          >
                            <span>
                              {status === 'all' ? 'All' : status === 'pending' ? 'Pending Review' : status.charAt(0).toUpperCase() + status.slice(1)}
                            </span>
                            <span className={cn(
                              "px-2 py-0.5 rounded-full text-xs font-semibold",
                              isActive && status === 'pending' && "bg-yellow-200 dark:bg-yellow-800 text-yellow-800 dark:text-yellow-200",
                              isActive && status === 'approved' && "bg-green-200 dark:bg-green-800 text-green-800 dark:text-green-200",
                              isActive && status === 'all' && "bg-purple-200 dark:bg-purple-800 text-purple-800 dark:text-purple-200",
                              !isActive && "bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-400"
                            )}>
                              {isLoadingGlossary ? '...' : count}
                            </span>
                          </button>
                        );
                      })}
                      {/* Show Rejected toggle - hidden by default */}
                      <button
                        onClick={() => {
                          setShowRejected(!showRejected);
                          if (!showRejected) {
                            setGlossaryStatusFilter('rejected');
                          } else if (glossaryStatusFilter === 'rejected') {
                            setGlossaryStatusFilter('all');
                          }
                        }}
                        className={cn(
                          "px-4 py-2 text-sm font-medium rounded-lg transition-all flex items-center space-x-2 border-2",
                          showRejected && "border-red-500 bg-red-50 dark:bg-red-900/20 text-red-700 dark:text-red-300",
                          !showRejected && "border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 text-gray-600 dark:text-gray-400 hover:border-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700"
                        )}
                        title="Show/hide rejected entries"
                      >
                        <span>Rejected</span>
                        <span className={cn(
                          "px-2 py-0.5 rounded-full text-xs font-semibold",
                          showRejected && "bg-red-200 dark:bg-red-800 text-red-800 dark:text-red-200",
                          !showRejected && "bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-400"
                        )}>
                          {glossaryEntries.filter((e: any) => e.status === 'rejected').length}
                        </span>
                      </button>
                    </div>
                    {showStatusHelp && (
                      <div className="mt-3 p-4 bg-blue-50 dark:bg-blue-900/20 rounded-lg border border-blue-200 dark:border-blue-800">
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-3 text-sm text-gray-700 dark:text-gray-300">
                          <div className="flex items-start space-x-2">
                            <span className="inline-block w-2 h-2 rounded-full bg-yellow-500 mt-1.5"></span>
                            <div>
                              <strong className="text-yellow-700 dark:text-yellow-300">Pending Review:</strong>
                              <p className="text-xs text-gray-600 dark:text-gray-400">Terms waiting for approval (includes both newly submitted and existing terms)</p>
                            </div>
                          </div>
                          <div className="flex items-start space-x-2">
                            <span className="inline-block w-2 h-2 rounded-full bg-green-500 mt-1.5"></span>
                            <div>
                              <strong className="text-green-700 dark:text-green-300">Approved:</strong>
                              <p className="text-xs text-gray-600 dark:text-gray-400">Terms that appear in tutorials (highlighted)</p>
                            </div>
                          </div>
                          <div className="flex items-start space-x-2">
                            <span className="inline-block w-2 h-2 rounded-full bg-red-500 mt-1.5"></span>
                            <div>
                              <strong className="text-red-700 dark:text-red-300">Rejected:</strong>
                              <p className="text-xs text-gray-600 dark:text-gray-400">Terms that were not approved (hidden by default, click "Rejected" button to view)</p>
                            </div>
                          </div>
                        </div>
                      </div>
                    )}
                  </div>
                )}
              </div>

              {/* Main Content Grid */}
              <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                {/* Left Column - Settings & Info */}
                <div className="lg:col-span-1 space-y-6">
                  {/* Keyword Highlighting Toggle */}
                  <div className="bg-white dark:bg-gray-800 rounded-lg shadow-md p-6">
                    <div className="flex items-center space-x-3 mb-4">
                      <BookOpen className="w-5 h-5 text-blue-600 dark:text-blue-400" />
                      <h3 className="text-lg font-semibold text-gray-900 dark:text-white">Highlighting</h3>
                    </div>
                    <div className="space-y-3">
                      <div className="flex items-center justify-between">
                        <div>
                          <h4 className="text-sm font-medium text-gray-900 dark:text-white">Enable Highlighting</h4>
                          <p className="text-xs text-gray-600 dark:text-gray-400 mt-1">
                            Show tooltips for technical terms
                          </p>
                        </div>
                        <label className="flex items-center cursor-pointer">
                          <div className="relative">
                            <input
                              type="checkbox"
                              className="sr-only"
                              checked={keywordFeatureEnabled}
                              onChange={handleToggleKeywordFeature}
                            />
                            <div
                              className={cn(
                                "block w-14 h-8 rounded-full transition-colors",
                                keywordFeatureEnabled ? "bg-blue-600" : "bg-gray-300 dark:bg-gray-600"
                              )}
                            />
                            <div
                              className={cn(
                                "dot absolute left-1 top-1 bg-white w-6 h-6 rounded-full transition-transform",
                                keywordFeatureEnabled ? "translate-x-6" : "translate-x-0"
                              )}
                            />
                          </div>
                        </label>
                      </div>
                    </div>
                  </div>

                  {/* How It Works (Collapsible) */}
                  {isAdminOrInstructor && (
                    <div className="bg-gradient-to-r from-purple-50 to-blue-50 dark:from-purple-900/20 dark:to-blue-900/20 rounded-lg shadow-md p-6 border border-purple-200 dark:border-purple-800">
                      <div className="flex items-center justify-between mb-3">
                        <div className="flex items-center space-x-2">
                          <Info className="w-5 h-5 text-purple-600 dark:text-purple-400" />
                          <h3 className="text-lg font-semibold text-gray-900 dark:text-white">How It Works</h3>
                        </div>
                        <button
                          onClick={() => setShowHowItWorks(!showHowItWorks)}
                          className="text-sm text-purple-600 dark:text-purple-400 hover:text-purple-700"
                        >
                          {showHowItWorks ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
                        </button>
                      </div>
                      {showHowItWorks && (
                        <div className="space-y-3 text-sm text-gray-700 dark:text-gray-300">
                          <div className="flex items-start space-x-2">
                            <span className="font-bold text-purple-600 mt-0.5">1.</span>
                            <span><strong>Extract:</strong> Click "Extract Technical Terms" when editing a tutorial</span>
                          </div>
                          <div className="flex items-start space-x-2">
                            <span className="font-bold text-purple-600 mt-0.5">2.</span>
                            <span><strong>Select:</strong> Choose which terms to submit for approval</span>
                          </div>
                          <div className="flex items-start space-x-2">
                            <span className="font-bold text-purple-600 mt-0.5">3.</span>
                            <span><strong>Approve:</strong> Review and approve/reject terms here</span>
                          </div>
                          <div className="flex items-start space-x-2">
                            <span className="font-bold text-purple-600 mt-0.5">4.</span>
                            <span><strong>Highlight:</strong> Approved terms appear in tutorials</span>
                          </div>
                        </div>
                      )}
                    </div>
                  )}
                </div>

                {/* Right Column - Entries List */}
                <div className="lg:col-span-2 space-y-6">

                  {/* Add New Entry Button */}
                  <div className="flex justify-end mb-4">
                    <button
                      onClick={() => setShowAddModal(true)}
                      className="flex items-center space-x-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors shadow-md"
                    >
                      <Plus className="w-4 h-4" />
                      <span>Add New Entry</span>
                    </button>
                  </div>

                  {/* Filters and Search */}
                  <div className="bg-white dark:bg-gray-800 rounded-lg shadow-md p-4 mb-6">
                    <div className="grid grid-cols-1 md:grid-cols-4 gap-3 mb-4">
                      <div className="relative">
                        <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
                        <input
                          type="text"
                          placeholder="Search entries..."
                          value={searchQuery}
                          onChange={(e) => setSearchQuery(e.target.value)}
                          className="w-full pl-10 pr-4 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                        />
                      </div>
                      <select
                        value={categoryFilter}
                        onChange={(e) => setCategoryFilter(e.target.value)}
                        className="px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                      >
                        <option value="">All Categories</option>
                        {Array.isArray(categories) && categories.map(category => (
                          <option key={category} value={category}>{category}</option>
                        ))}
                      </select>
                      <select
                        value={tagFilter}
                        onChange={(e) => setTagFilter(e.target.value)}
                        className="px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                      >
                        <option value="">All Tags</option>
                        {Array.isArray(tags) && tags.map(tag => (
                          <option key={tag} value={tag}>{tag}</option>
                        ))}
                      </select>
                      <select
                        value={statusFilter}
                        onChange={(e) => setStatusFilter(e.target.value as 'all' | 'active' | 'inactive')}
                        className="px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                      >
                        <option value="all">All Status</option>
                        <option value="active">Active Only</option>
                        <option value="inactive">Inactive Only</option>
                      </select>
                    </div>

                    {/* Bulk Actions */}
                    <div className="flex items-center justify-between pt-3 border-t border-gray-200 dark:border-gray-700">
                      <div className="flex items-center space-x-2 flex-wrap gap-2">
                        <button
                          onClick={handleSelectAllGlossary}
                          className="flex items-center space-x-2 px-3 py-1.5 text-sm bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 rounded-md hover:bg-gray-200 dark:hover:bg-gray-600 transition-colors"
                        >
                          {selectedEntries.size === filteredEntries.length ? (
                            <CheckSquare className="w-4 h-4" />
                          ) : (
                            <Square className="w-4 h-4" />
                          )}
                          <span>{selectedEntries.size === filteredEntries.length ? 'Deselect' : 'Select'} All</span>
                        </button>
                        {selectedEntries.size > 0 && isAdminOrInstructor && (
                          <>
                            <button
                              onClick={handleBulkApproveGlossary}
                              className="flex items-center space-x-2 px-3 py-1.5 text-sm bg-green-600 text-white rounded-md hover:bg-green-700 transition-colors"
                            >
                              <CheckCircle className="w-4 h-4" />
                              <span>Approve ({selectedEntries.size})</span>
                            </button>
                            <button
                              onClick={handleBulkRejectGlossary}
                              className="flex items-center space-x-2 px-3 py-1.5 text-sm bg-orange-600 text-white rounded-md hover:bg-orange-700 transition-colors"
                            >
                              <X className="w-4 h-4" />
                              <span>Reject ({selectedEntries.size})</span>
                            </button>
                          </>
                        )}
                        {selectedEntries.size > 0 && (
                          <button
                            onClick={handleBulkDeleteGlossary}
                            className="flex items-center space-x-2 px-3 py-1.5 text-sm bg-red-600 text-white rounded-md hover:bg-red-700 transition-colors"
                          >
                            <Trash2 className="w-4 h-4" />
                            <span>Delete ({selectedEntries.size})</span>
                          </button>
                        )}
                      </div>
                      <div className="flex items-center space-x-4">
                        <div className="text-sm font-medium text-gray-600 dark:text-gray-400">
                          {filteredEntries.length} {filteredEntries.length === 1 ? 'entry' : 'entries'}
                        </div>
                        {isAdminOrInstructor && glossaryStatusFilter === 'pending' && filteredEntries.length > 0 && (
                          <div className="flex items-center space-x-2">
                            <button
                              onClick={handleApproveAllFiltered}
                              className="flex items-center space-x-1 px-3 py-1.5 text-xs font-semibold bg-green-600 text-white rounded-md hover:bg-green-700 transition-colors"
                              title={`Approve all ${filteredEntries.length} entries in current view`}
                            >
                              <CheckCircle className="w-3 h-3" />
                              <span>Approve All</span>
                            </button>
                            <button
                              onClick={handleRejectAllFiltered}
                              className="flex items-center space-x-1 px-3 py-1.5 text-xs font-semibold bg-red-600 text-white rounded-md hover:bg-red-700 transition-colors"
                              title={`Reject all ${filteredEntries.length} entries in current view`}
                            >
                              <X className="w-3 h-3" />
                              <span>Reject All</span>
                            </button>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>


                  {/* Entries List */}
                  <div className="bg-white dark:bg-gray-800 rounded-lg shadow-md p-6">
                    <div className="flex items-center justify-between mb-4">
                      <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
                        Glossary Entries {isLoadingGlossary ? '(Loading...)' : `(${filteredEntries.length})`}
                      </h3>
                    </div>
                {isLoadingGlossary ? (
                  <div className="flex items-center justify-center py-12">
                    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-purple-600"></div>
                    <span className="ml-3 text-gray-600 dark:text-gray-400">Loading entries...</span>
                  </div>
                ) : filteredEntries.length === 0 ? (
                  <div className="text-center py-12">
                    <Database className="w-16 h-16 text-gray-300 dark:text-gray-600 mx-auto mb-4" />
                    <p className="text-lg font-medium text-gray-900 dark:text-white mb-2">No entries found</p>
                    <p className="text-sm text-gray-600 dark:text-gray-400 mb-4">
                      {glossaryStatusFilter === 'all' 
                        ? "Get started by extracting terms from a tutorial or adding a new entry manually."
                        : glossaryStatusFilter === 'pending'
                        ? "No terms are currently pending approval."
                        : glossaryStatusFilter === 'needs_review'
                        ? "All existing terms have been reviewed."
                        : `No ${glossaryStatusFilter} entries found.`}
                    </p>
                    {glossaryStatusFilter === 'all' && (
                      <div className="flex flex-col sm:flex-row gap-3 justify-center items-center">
                        <p className="text-sm text-gray-500">Try:</p>
                        <button
                          onClick={() => setShowAddModal(true)}
                          className="px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition-colors text-sm"
                        >
                          Add Entry Manually
                        </button>
                        <span className="text-gray-400">or</span>
                        <span className="text-sm text-gray-600">Extract terms from a tutorial</span>
                      </div>
                    )}
                  </div>
                ) : (
                  <div className="space-y-3">
                    {filteredEntries.map((entry) => (
                      <div
                        key={entry.id}
                        className={cn(
                          "flex items-start space-x-3 p-4 rounded-lg border transition-colors",
                          selectedEntries.has(entry.id)
                            ? "bg-blue-50 dark:bg-blue-900/20 border-blue-200 dark:border-blue-800"
                            : "bg-gray-50 dark:bg-gray-700 border-gray-200 dark:border-gray-600"
                        )}
                      >
                        <div className="mt-1 relative flex-shrink-0">
                          <input
                            type="checkbox"
                            checked={selectedEntries.has(entry.id)}
                            onChange={() => handleToggleGlossarySelection(entry.id)}
                            className="w-5 h-5 rounded border-2 border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 text-purple-600 focus:ring-2 focus:ring-purple-500 focus:ring-offset-2 cursor-pointer transition-all appearance-none checked:bg-purple-600 checked:border-purple-600"
                            style={{
                              accentColor: '#9333ea'
                            }}
                          />
                          {selectedEntries.has(entry.id) && (
                            <Check
                              className="absolute top-0.5 left-0.5 w-4 h-4 text-white pointer-events-none"
                              strokeWidth={3}
                            />
                          )}
                        </div>
                        <div className="flex-1">
                          <div className="flex items-center space-x-2 mb-2 flex-wrap gap-2">
                            <h5 className="font-semibold text-gray-900 dark:text-white">{entry.term}</h5>
                            {entry.isActive ? (
                              <Eye className="w-4 h-4 text-green-500" />
                            ) : (
                              <EyeOff className="w-4 h-4 text-gray-400" />
                            )}
                            {/* Status Badge */}
                            {(entry as any).status && (
                              <span className={cn(
                                "inline-block text-xs px-2 py-0.5 rounded-full font-medium",
                                (entry as any).status === 'approved' ? "bg-green-100 dark:bg-green-900/50 text-green-800 dark:text-green-200 border border-green-300 dark:border-green-700" :
                                (entry as any).status === 'pending' ? "bg-yellow-100 dark:bg-yellow-900/50 text-yellow-800 dark:text-yellow-200 border border-yellow-300 dark:border-yellow-700" :
                                (entry as any).status === 'rejected' ? "bg-red-100 dark:bg-red-900/50 text-red-800 dark:text-red-200 border border-red-300 dark:border-red-700" :
                                (entry as any).status === 'needs_review' ? "bg-orange-100 dark:bg-orange-900/50 text-orange-800 dark:text-orange-200 border border-orange-300 dark:border-orange-700" :
                                "bg-gray-100 dark:bg-gray-700 text-gray-800 dark:text-gray-200"
                              )}>
                                {(entry as any).status === 'needs_review' ? 'Needs Review' : (entry as any).status.charAt(0).toUpperCase() + (entry as any).status.slice(1)}
                              </span>
                            )}
                            {entry.category && (
                              <span className="inline-block bg-blue-100 dark:bg-blue-900/50 text-blue-800 dark:text-blue-200 text-xs px-2 py-0.5 rounded-full">
                                {entry.category}
                              </span>
                            )}
                          </div>
                          <p className="text-sm text-gray-700 dark:text-gray-300 mb-2">{entry.definition}</p>
                          <div className="flex flex-wrap gap-2 text-xs">
                            {entry.aliases && entry.aliases.length > 0 && (
                              <div className="flex items-center space-x-1">
                                <span className="text-gray-500">Aliases:</span>
                                <span className="text-gray-600 dark:text-gray-400">{entry.aliases.join(', ')}</span>
                              </div>
                            )}
                            {entry.tags && entry.tags.length > 0 && (
                              <div className="flex items-center space-x-1">
                                <Tag className="w-3 h-3 text-gray-500" />
                                <span className="text-gray-600 dark:text-gray-400">{entry.tags.join(', ')}</span>
                              </div>
                            )}
                          </div>
                        </div>
                        <div className="flex space-x-2">
                          <button
                            onClick={() => handleEditGlossaryEntry(entry)}
                            className="p-2 rounded-full text-blue-600 hover:bg-blue-100 dark:hover:bg-blue-900/50 transition-colors"
                            title="Edit Entry"
                          >
                            <Edit className="w-4 h-4" />
                          </button>
                          <button
                            onClick={() => handleDeleteGlossaryEntry(entry.id)}
                            className="p-2 rounded-full text-red-600 hover:bg-red-100 dark:hover:bg-red-900/50 transition-colors"
                            title="Delete Entry"
                          >
                            <Trash2 className="w-4 h-4" />
                          </button>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
                  </div>
                </div>
              </div>
            </motion.div>
          )}

          {activeTab === 'voiceover' && (
            <motion.div
              key="voiceover"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -20 }}
              className="space-y-6"
            >
              {/* Voiceover Settings */}
              <div className="bg-white dark:bg-gray-800 rounded-lg shadow-md p-6">
                <div className="flex items-center space-x-3 mb-6">
                  <Volume2 className="w-6 h-6 text-blue-600 dark:text-blue-400" />
                  <h2 className="text-xl font-semibold text-gray-900 dark:text-white">Voiceover Settings</h2>
                </div>
                
                <div className="space-y-6">
                  {/* Enable Voiceover */}
                  <div className="flex items-center justify-between">
                    <div>
                      <h3 className="text-lg font-medium text-gray-900 dark:text-white">Enable Voiceover</h3>
                      <p className="text-sm text-gray-600 dark:text-gray-400">
                        Enable text-to-speech for tutorial steps
                      </p>
                    </div>
                    <label className="flex items-center cursor-pointer">
                      <div className="relative">
                        <input
                          type="checkbox"
                          className="sr-only"
                          checked={voiceoverSettings.enabled}
                          onChange={(e) => handleVoiceoverSettingChange('enabled', e.target.checked)}
                        />
                        <div
                          className={cn(
                            "block w-14 h-8 rounded-full transition-colors",
                            voiceoverSettings.enabled ? "bg-blue-600" : "bg-gray-300 dark:bg-gray-600"
                          )}
                        />
                        <div
                          className={cn(
                            "dot absolute left-1 top-1 bg-white w-6 h-6 rounded-full transition-transform",
                            voiceoverSettings.enabled ? "translate-x-6" : "translate-x-0"
                          )}
                        />
                      </div>
                    </label>
                  </div>

                  {voiceoverSettings.enabled && (
                    <>
                      {/* Provider Selection */}
                      <div>
                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                          Voice Provider
                        </label>
                        <select
                          value={voiceoverSettings.provider}
                          onChange={(e) => handleVoiceoverSettingChange('provider', e.target.value)}
                          className="w-full p-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                        >
                          <option value="elevenlabs">ElevenLabs (Premium)</option>
                          <option value="browser">Browser TTS (Free)</option>
                        </select>
                      </div>

                      {/* Voice Selection */}
                      <div>
                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                          Voice
                        </label>
                        {isLoadingVoices ? (
                          <div className="text-sm text-gray-500">Loading voices...</div>
                        ) : (
                          <select
                            value={voiceoverSettings.voice}
                            onChange={(e) => handleVoiceoverSettingChange('voice', e.target.value)}
                            className="w-full p-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                          >
                            {availableVoices
                              .filter(voice => voice.provider === voiceoverSettings.provider)
                              .map(voice => (
                                <option key={`${voice.provider}-${voice.id}`} value={voice.id}>
                                  {voice.name}
                                </option>
                              ))}
                          </select>
                        )}
                      </div>

                      {/* Speed Control */}
                      <div>
                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                          Speed: {voiceoverSettings.speed}x
                        </label>
                        <input
                          type="range"
                          min="0.5"
                          max="2.0"
                          step="0.1"
                          value={voiceoverSettings.speed}
                          onChange={(e) => handleVoiceoverSettingChange('speed', parseFloat(e.target.value))}
                          className="w-full"
                        />
                      </div>

                      {/* Pitch Control */}
                      <div>
                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                          Pitch: {voiceoverSettings.pitch}x
                        </label>
                        <input
                          type="range"
                          min="0.5"
                          max="2.0"
                          step="0.1"
                          value={voiceoverSettings.pitch}
                          onChange={(e) => handleVoiceoverSettingChange('pitch', parseFloat(e.target.value))}
                          className="w-full"
                        />
                      </div>

                      {/* Volume Control */}
                      <div>
                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                          Volume: {Math.round(voiceoverSettings.volume * 100)}%
                        </label>
                        <input
                          type="range"
                          min="0"
                          max="1"
                          step="0.1"
                          value={voiceoverSettings.volume}
                          onChange={(e) => handleVoiceoverSettingChange('volume', parseFloat(e.target.value))}
                          className="w-full"
                        />
                      </div>

                      {/* Test Section */}
                      <div className="border-t border-gray-200 dark:border-gray-700 pt-6">
                        <h4 className="text-lg font-medium text-gray-900 dark:text-white mb-4">Test Voiceover</h4>
                        <div className="space-y-4">
                          <div>
                            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                              Test Text
                            </label>
                            <textarea
                              value={testText}
                              onChange={(e) => setTestText(e.target.value)}
                              rows={3}
                              className="w-full p-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-white resize-y"
                              placeholder="Enter text to test the voiceover settings..."
                            />
                          </div>
                          <button
                            onClick={testVoiceover}
                            className="flex items-center space-x-2 px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors"
                          >
                            <Play className="w-4 h-4" />
                            <span>Test Voiceover</span>
                          </button>
                        </div>
                      </div>
                    </>
                  )}
                </div>
              </div>
            </motion.div>
          )}

          {activeTab === 'keywords' && (
            <motion.div
              key="keywords"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -20 }}
              className="space-y-6"
            >
              {/* Keyword Feature Settings */}
              <div className="bg-white dark:bg-gray-800 rounded-lg shadow-md p-6">
                <div className="flex items-center space-x-3 mb-6">
                  <BookOpen className="w-6 h-6 text-blue-600 dark:text-blue-400" />
                  <h2 className="text-xl font-semibold text-gray-900 dark:text-white">Keyword Highlighting</h2>
                </div>
                
                <div className="space-y-4">
                  <div className="flex items-center justify-between">
                    <div>
                      <h3 className="text-lg font-medium text-gray-900 dark:text-white">Enable Keyword Highlighting</h3>
                      <p className="text-sm text-gray-600 dark:text-gray-400">
                        Show tooltips and explanations for technical terms in tutorials.
                      </p>
                    </div>
                    <label className="flex items-center cursor-pointer">
                      <div className="relative">
                        <input
                          type="checkbox"
                          className="sr-only"
                          checked={keywordFeatureEnabled}
                          onChange={handleToggleKeywordFeature}
                        />
                        <div
                          className={cn(
                            "block w-14 h-8 rounded-full transition-colors",
                            keywordFeatureEnabled ? "bg-blue-600" : "bg-gray-300 dark:bg-gray-600"
                          )}
                        />
                        <div
                          className={cn(
                            "dot absolute left-1 top-1 bg-white w-6 h-6 rounded-full transition-transform",
                            keywordFeatureEnabled ? "translate-x-6" : "translate-x-0"
                          )}
                        />
                      </div>
                    </label>
                  </div>

                  <div className="pt-4 border-t border-gray-200 dark:border-gray-700">
                    <p className="text-sm text-gray-600 dark:text-gray-400">
                      When enabled, technical terms will be highlighted with blue underlines and show explanations on hover.
                    </p>
                  </div>
                </div>
              </div>

              {/* Add New Keyword Form */}
              <div className="bg-white dark:bg-gray-800 rounded-lg shadow-md p-6">
                <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">Add New Keyword</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Word/Phrase *</label>
                    <input
                      type="text"
                      value={newKeyword.word}
                      onChange={(e) => setNewKeyword({ ...newKeyword, word: e.target.value })}
                      className="w-full p-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                      placeholder="e.g., BOM"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Category</label>
                    <input
                      type="text"
                      value={newKeyword.category}
                      onChange={(e) => setNewKeyword({ ...newKeyword, category: e.target.value })}
                      className="w-full p-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                      placeholder="e.g., Manufacturing"
                    />
                  </div>
                </div>
                <div className="mb-4">
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Explanation *</label>
                  <textarea
                    value={newKeyword.explanation}
                    onChange={(e) => setNewKeyword({ ...newKeyword, explanation: e.target.value })}
                    rows={3}
                    className="w-full p-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-white resize-y"
                    placeholder="Detailed explanation of the term"
                  />
                </div>
                <div className="mb-4">
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Aliases (comma-separated)</label>
                  <input
                    type="text"
                    value={newKeyword.aliases}
                    onChange={(e) => setNewKeyword({ ...newKeyword, aliases: e.target.value })}
                    className="w-full p-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                    placeholder="e.g., Bill of Materials, BoM"
                  />
                </div>
                <div className="flex justify-end">
                  <button
                    onClick={handleAddKeyword}
                    className="flex items-center space-x-2 px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors"
                  >
                    <Plus className="w-4 h-4" />
                    <span>Add Keyword</span>
                  </button>
                </div>
              </div>

              {/* Keywords List */}
              <div className="bg-white dark:bg-gray-800 rounded-lg shadow-md p-6">
                <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">
                  Keyword Definitions ({keywordDefinitions.length})
                </h3>
                {keywordDefinitions.length === 0 ? (
                  <p className="text-gray-600 dark:text-gray-400">No keywords defined yet.</p>
                ) : (
                  <div className="space-y-3">
                    {keywordDefinitions.map((keyword, index) => (
                      <div
                        key={index}
                        className="flex items-start justify-between p-4 bg-gray-50 dark:bg-gray-700 rounded-lg border border-gray-200 dark:border-gray-600"
                      >
                        <div className="flex-1">
                          <div className="flex items-center space-x-2 mb-2">
                            <h5 className="font-semibold text-gray-900 dark:text-white">{keyword.word}</h5>
                            {keyword.category && (
                              <span className="inline-block bg-blue-100 dark:bg-blue-900/50 text-blue-800 dark:text-blue-200 text-xs px-2 py-0.5 rounded-full">
                                {keyword.category}
                              </span>
                            )}
                          </div>
                          <p className="text-sm text-gray-700 dark:text-gray-300 mb-2">{keyword.explanation}</p>
                          {keyword.aliases && keyword.aliases.length > 0 && (
                            <div className="flex flex-wrap gap-1">
                              {keyword.aliases.map((alias, aliasIndex) => (
                                <span
                                  key={aliasIndex}
                                  className="px-2 py-1 bg-gray-100 dark:bg-gray-600 text-gray-700 dark:text-gray-300 text-xs rounded"
                                >
                                  {alias}
                                </span>
                              ))}
                            </div>
                          )}
                        </div>
                        <div className="flex space-x-2">
                          <button
                            onClick={() => setEditingKeyword(keyword)}
                            className="p-2 rounded-full text-blue-600 hover:bg-blue-100 dark:hover:bg-blue-900/50 transition-colors"
                            title="Edit Keyword"
                          >
                            <Edit className="w-4 h-4" />
                          </button>
                          <button
                            onClick={() => handleDeleteKeyword(keyword.word)}
                            className="p-2 rounded-full text-red-600 hover:bg-red-100 dark:hover:bg-red-900/50 transition-colors"
                            title="Delete Keyword"
                          >
                            <Trash2 className="w-4 h-4" />
                          </button>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </motion.div>
          )}
        </AnimatePresence>

        {/* Import Modal */}
        {showImportModal && (
          <div className="fixed inset-0 bg-gray-900 bg-opacity-75 flex items-center justify-center z-50 p-4">
            <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl w-full max-w-2xl">
              <div className="p-6">
                <h4 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">Import Entries</h4>
                <textarea
                  value={importData}
                  onChange={(e) => setImportData(e.target.value)}
                  rows={10}
                  className="w-full p-3 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-white font-mono text-sm"
                  placeholder="Paste JSON data here..."
                />
                {importResults && (
                  <div className="mt-4 p-4 rounded-md bg-gray-50 dark:bg-gray-700">
                    <div className="flex items-center space-x-2 mb-2">
                      {importResults.success > 0 ? (
                        <CheckCircle className="w-5 h-5 text-green-500" />
                      ) : (
                        <AlertCircle className="w-5 h-5 text-red-500" />
                      )}
                      <span className="font-semibold">
                        Import Results: {importResults.success} successful, {importResults.errors.length} errors
                      </span>
                    </div>
                    {importResults.errors.length > 0 && (
                      <div className="text-sm text-red-600 dark:text-red-400">
                        <ul className="list-disc list-inside space-y-1">
                          {importResults.errors.map((error, index) => (
                            <li key={index}>{error}</li>
                          ))}
                        </ul>
                      </div>
                    )}
                  </div>
                )}
                <div className="flex justify-end space-x-2 mt-4">
                  <button
                    onClick={() => {
                      setShowImportModal(false);
                      setImportData('');
                      setImportResults(null);
                    }}
                    className="px-4 py-2 bg-gray-200 dark:bg-gray-700 text-gray-800 dark:text-gray-200 rounded-md hover:bg-gray-300 dark:hover:bg-gray-600 transition-colors"
                  >
                    Cancel
                  </button>
                  <button
                    onClick={handleImportGlossary}
                    className="px-4 py-2 bg-green-600 text-white rounded-md hover:bg-green-700 transition-colors"
                  >
                    Import
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Add New Entry Modal */}
        <AnimatePresence>
          {showAddModal && (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4"
              onClick={() => setShowAddModal(false)}
            >
              <motion.div
                initial={{ scale: 0.95, opacity: 0 }}
                animate={{ scale: 1, opacity: 1 }}
                exit={{ scale: 0.95, opacity: 0 }}
                onClick={(e) => e.stopPropagation()}
                className="bg-white dark:bg-gray-800 rounded-lg shadow-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto"
              >
                <div className="sticky top-0 bg-white dark:bg-gray-800 border-b border-gray-200 dark:border-gray-700 px-6 py-4 flex items-center justify-between z-10">
                  <div className="flex items-center space-x-3">
                    <Plus className="w-5 h-5 text-blue-600 dark:text-blue-400" />
                    <h3 className="text-xl font-semibold text-gray-900 dark:text-white">Add New Entry</h3>
                  </div>
                  <div className="flex items-center space-x-3">
                    {isAdminOrInstructor && (
                      <span className="text-xs px-2 py-1 bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-300 rounded">
                        Auto-approved
                      </span>
                    )}
                    <button
                      onClick={() => setShowAddModal(false)}
                      className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
                    >
                      <X className="w-5 h-5" />
                    </button>
                  </div>
                </div>
                <div className="p-6 space-y-4">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Term *</label>
                      <input
                        type="text"
                        value={newEntry.term}
                        onChange={(e) => setNewEntry({ ...newEntry, term: e.target.value })}
                        className="w-full p-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                        placeholder="e.g., BOM"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Category</label>
                      <input
                        type="text"
                        value={newEntry.category}
                        onChange={(e) => setNewEntry({ ...newEntry, category: e.target.value })}
                        className="w-full p-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                        placeholder="e.g., Manufacturing"
                      />
                    </div>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Definition *</label>
                    <textarea
                      value={newEntry.definition}
                      onChange={(e) => setNewEntry({ ...newEntry, definition: e.target.value })}
                      rows={3}
                      className="w-full p-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-white resize-y"
                      placeholder="Detailed explanation of the term"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Image URL (Optional)</label>
                    <input
                      type="url"
                      value={newEntry.image}
                      onChange={(e) => setNewEntry({ ...newEntry, image: e.target.value })}
                      className="w-full p-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                      placeholder="https://example.com/image.jpg"
                    />
                    {newEntry.image && (
                      <div className="mt-2">
                        <img
                          src={newEntry.image}
                          alt="Preview"
                          className="w-32 h-24 object-cover rounded border border-gray-200"
                          onError={(e) => {
                            (e.target as HTMLImageElement).style.display = 'none';
                          }}
                        />
                      </div>
                    )}
                  </div>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Aliases (comma-separated)</label>
                      <input
                        type="text"
                        value={newEntry.aliases}
                        onChange={(e) => setNewEntry({ ...newEntry, aliases: e.target.value })}
                        className="w-full p-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                        placeholder="e.g., Bill of Materials, BoM"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Tags (comma-separated)</label>
                      <input
                        type="text"
                        value={newEntry.tags}
                        onChange={(e) => setNewEntry({ ...newEntry, tags: e.target.value })}
                        className="w-full p-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                        placeholder="e.g., manufacturing, production"
                      />
                    </div>
                  </div>
                  <div className="flex items-center justify-between pt-4 border-t border-gray-200 dark:border-gray-700">
                    <label className="flex items-center space-x-2">
                      <input
                        type="checkbox"
                        checked={newEntry.isActive}
                        onChange={(e) => setNewEntry({ ...newEntry, isActive: e.target.checked })}
                        className="rounded"
                      />
                      <span className="text-sm text-gray-700 dark:text-gray-300">Active</span>
                    </label>
                    <div className="flex space-x-3">
                      <button
                        onClick={() => setShowAddModal(false)}
                        className="px-4 py-2 text-gray-700 dark:text-gray-300 bg-gray-100 dark:bg-gray-700 rounded-md hover:bg-gray-200 dark:hover:bg-gray-600 transition-colors"
                      >
                        Cancel
                      </button>
                      <button
                        onClick={handleAddGlossaryEntry}
                        className="flex items-center space-x-2 px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors"
                      >
                        <Plus className="w-4 h-4" />
                        <span>Add Entry</span>
                      </button>
                    </div>
                  </div>
                </div>
              </motion.div>
            </motion.div>
          )}
        </AnimatePresence>

        {/* Edit Entry Modal */}
        <AnimatePresence>
          {showEditModal && editingEntry && (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4"
              onClick={() => setShowEditModal(false)}
            >
              <motion.div
                initial={{ scale: 0.9, opacity: 0 }}
                animate={{ scale: 1, opacity: 1 }}
                exit={{ scale: 0.9, opacity: 0 }}
                className="bg-white dark:bg-gray-800 rounded-lg shadow-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto"
                onClick={(e) => e.stopPropagation()}
              >
                <div className="p-6">
                  <div className="flex items-center justify-between mb-6">
                    <h3 className="text-xl font-semibold text-gray-900 dark:text-white">
                      Edit Entry: {editingEntry.term}
                    </h3>
                    <button
                      onClick={() => setShowEditModal(false)}
                      className="p-2 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
                    >
                      <X className="w-5 h-5" />
                    </button>
                  </div>

                  <div className="space-y-4">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Term *</label>
                        <input
                          type="text"
                          value={newEntry.term}
                          onChange={(e) => setNewEntry({ ...newEntry, term: e.target.value })}
                          className="w-full p-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                          placeholder="e.g., BOM"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Category</label>
                        <input
                          type="text"
                          value={newEntry.category}
                          onChange={(e) => setNewEntry({ ...newEntry, category: e.target.value })}
                          className="w-full p-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                          placeholder="e.g., Manufacturing"
                        />
                      </div>
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Definition *</label>
                      <textarea
                        value={newEntry.definition}
                        onChange={(e) => setNewEntry({ ...newEntry, definition: e.target.value })}
                        rows={3}
                        className="w-full p-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-white resize-y"
                        placeholder="Detailed explanation of the term"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Image URL (Optional)</label>
                      <input
                        type="url"
                        value={newEntry.image}
                        onChange={(e) => setNewEntry({ ...newEntry, image: e.target.value })}
                        className="w-full p-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                        placeholder="https://example.com/image.jpg"
                      />
                      {newEntry.image && (
                        <div className="mt-2">
                          <img
                            src={newEntry.image}
                            alt="Preview"
                            className="w-32 h-24 object-cover rounded border border-gray-200"
                            onError={(e) => {
                              (e.target as HTMLImageElement).style.display = 'none';
                            }}
                          />
                        </div>
                      )}
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Aliases (comma-separated)</label>
                        <input
                          type="text"
                          value={newEntry.aliases}
                          onChange={(e) => setNewEntry({ ...newEntry, aliases: e.target.value })}
                          className="w-full p-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                          placeholder="e.g., Bill of Materials, BoM"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Tags (comma-separated)</label>
                        <input
                          type="text"
                          value={newEntry.tags}
                          onChange={(e) => setNewEntry({ ...newEntry, tags: e.target.value })}
                          className="w-full p-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                          placeholder="e.g., manufacturing, production"
                        />
                      </div>
                    </div>

                    <div className="flex items-center justify-between">
                      <label className="flex items-center space-x-2">
                        <input
                          type="checkbox"
                          checked={newEntry.isActive}
                          onChange={(e) => setNewEntry({ ...newEntry, isActive: e.target.checked })}
                          className="rounded"
                        />
                        <span className="text-sm text-gray-700 dark:text-gray-300">Active</span>
                      </label>
                    </div>
                  </div>

                  <div className="flex justify-end space-x-3 mt-6">
                    <button
                      onClick={handleCancelEdit}
                      className="px-4 py-2 text-gray-700 dark:text-gray-300 bg-gray-100 dark:bg-gray-700 rounded-md hover:bg-gray-200 dark:hover:bg-gray-600 transition-colors"
                    >
                      Cancel
                    </button>
                    <button
                      onClick={handleSaveGlossaryEntry}
                      className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors flex items-center space-x-2"
                    >
                      <Save className="w-4 h-4" />
                      <span>Save Changes</span>
                    </button>
                  </div>
                </div>
              </motion.div>
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </div>
  );
};

export default SettingsPage;
